/*
 * Dice Tower System — à la carte individual parts shop.
 *
 * Reuses the same visual/interaction techniques as the main bundle
 * configurator (assets/dice-tower-configurator.js) — the color hex map,
 * flat-color material application, and camera framing are copied here as
 * their own small, self-contained implementation rather than imported from
 * that file, since that file is live-critical for the real $75/$110
 * checkout flow and refactoring it for reuse isn't worth the regression
 * risk for a one-time code-sharing win.
 *
 * Two card types:
 * - Exact-match cards (data-shop-part-card): every real color option on
 *   the product gets its own swatch row; picking a value per option finds
 *   the one real matching variant and prices/add-to-carts exactly that
 *   variant. Used for every panel/vault piece (1 option) and the Standard
 *   Front Top Panel (2 options, 225 real variants).
 * - Zone cards (data-shop-zone-card): Tray Body and Clamp are single-
 *   variant products (a true free 3-zone × 15-color product would need
 *   3,375 variants, over Shopify's 2,048 cap) — their swatch UI is client-
 *   side only, and Add to Cart always adds that one real variant with a
 *   "Colors" property describing the actual picks, matching how the main
 *   configurator already sells these two parts today.
 *
 * Cards lazy-load their 3D model (via IntersectionObserver) instead of all
 * loading at once — this page has no tab filter hiding most cards like the
 * bundle configurator does, so eagerly loading all 21 models up front would
 * be a real mobile performance problem.
 */
(function () {
  'use strict';

  // F9.2 fix: same reordering as COLORS in dice-tower-configurator.js —
  // keep the two in sync if this list ever changes. Spread apart so the 3
  // visually similar muted grayish/earthy tones (Silver, Gray, Bronze)
  // aren't adjacent in the rendered swatch row. No colors added/removed.
  var COLORS = [
    { name: 'Jade White', hex: '#FFFFFF' },
    { name: 'Gold', hex: '#E4BD68' },
    { name: 'Silver', hex: '#A6A9AA' },
    { name: 'Red', hex: '#C12E1F' },
    { name: 'Gray', hex: '#8E9089' },
    { name: 'Orange', hex: '#FF6A13' },
    { name: 'Bronze', hex: '#847D48' },
    { name: 'Yellow', hex: '#F4EE2A' },
    { name: 'Brown', hex: '#9D432C' },
    { name: 'Bambu Green', hex: '#00AE42' },
    { name: 'Pink', hex: '#F55A74' },
    { name: 'Cyan', hex: '#0086D6' },
    { name: 'Blue', hex: '#0A2989' },
    { name: 'Purple', hex: '#5E43B7' },
    { name: 'Black', hex: '#000000' }
  ];
  var COLOR_HEX = {};
  COLORS.forEach(function (c) { COLOR_HEX[c.name] = c.hex; });

  // Zone labels/order match MULTICOLOR_ZONES in dice-tower-configurator.js.
  // Every zone here defaults to Silver, matching the configurator's own
  // Silver-everywhere default, with the same two exceptions: the personal
  // tray (tray-body/tray-clamp below are copied directly from the
  // configurator's own MULTICOLOR_ZONES defaults, not independently chosen)
  // and the Standard Front Top Panel's Logo Color, which is handled
  // separately below via DEFAULT_OPTION_VALUE_OVERRIDES since that's a real
  // Shopify variant option, not a ZONE_DEFS zone card.
  var ZONE_DEFS = {
    'tray-body': [
      { label: 'Top and Bottom', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Trim Accent', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inner Band', hex: '#A6A9AA', name: 'Silver' }
    ],
    'tray-clamp': [
      { label: 'Top and Bottom', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Trim Accent', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Top Panel and Middle Body', hex: '#000000', name: 'Black' }
    ],

    // Hex pattern insert pieces — zone labels/order match MULTICOLOR_ZONES
    // in dice-tower-configurator.js. Left/Right Side Insert share one zone
    // list since they're the same physical geometry, just mirrored.
    'front-bottom-insert': [
      { label: 'Three Top Hexagons', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle Hexagons', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Three Bottom Hexagons', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'front-top-insert': [
      { label: 'Top and Bottom Hexagons', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Two Top Hexagons', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Two Bottom Hexagons', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'side-insert': [
      { label: 'Bottom Outer Rim Hexagons', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle Hexagons', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Top Outer Rim Hexagons', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'lid-insert': [
      { label: 'Accent Trim', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle Hexagons', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Outer Hexagons', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],

    // Kumiko/Inverse Weave/Regal — zone labels/order match the matching
    // '<role>-<pattern>' entries in MULTICOLOR_ZONES in
    // dice-tower-configurator.js (this file keeps its own independent copy,
    // same as it already did for Hex above — not imported).
    'front-bottom-insert-kumiko': [
      { label: 'Background', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Hexagons', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'front-top-insert-kumiko': [
      { label: 'Background', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Hexagons', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'side-insert-kumiko': [
      { label: 'Background', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Hexagons', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'lid-insert-kumiko': [
      { label: 'Background', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Hexagons', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],

    'front-bottom-insert-inverse-weave': [
      { label: 'Inside Bottom', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Middle', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Top', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'front-top-insert-inverse-weave': [
      { label: 'Inside Bottom', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Middle', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Top', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'side-insert-inverse-weave': [
      { label: 'Inside Bottom', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Middle', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Top', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'lid-insert-inverse-weave': [
      { label: 'Inside Bottom', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Middle', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Top', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],

    'front-bottom-insert-regal': [
      { label: 'Square', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Upside Down Triangle', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Rightside Up Triangle', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'front-top-insert-regal': [
      { label: 'Square', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Upside Down Triangle', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Rightside Up Triangle', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'side-insert-regal': [
      { label: 'Square', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Upside Down Triangle', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Rightside Up Triangle', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'lid-insert-regal': [
      { label: 'Square', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Upside Down Triangle', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Rightside Up Triangle', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],

    // Wicker/Wyrmcron — zone labels/order match the matching
    // '<role>-<pattern>' entries in MULTICOLOR_ZONES in
    // dice-tower-configurator.js. Unlike every pattern above (3-4 zones),
    // these are genuine 2-material AMS prints (verified per-part from each
    // source .3mf's own <component> zones).
    'front-bottom-insert-wicker': [
      { label: 'Border', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'front-top-insert-wicker': [
      { label: 'Border', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'side-insert-wicker': [
      { label: 'Border', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'lid-insert-wicker': [
      { label: 'Border', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],

    // Wyrmcron's third zone ("Pattern") was painted directly onto the
    // Background mesh in the source .3mf rather than being a separate
    // object — see the matching comment in dice-tower-configurator.js's
    // MULTICOLOR_ZONES for the extraction/naming story. Defaults to Silver,
    // matching every other zone (originally defaulted to Black, matching
    // the actual filament color it was painted with, but the store owner
    // later asked for Silver everywhere except the Standard front-top Logo
    // zone and the personal tray).
    'front-bottom-insert-wyrmcron': [
      { label: 'Border', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Background', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'front-top-insert-wyrmcron': [
      { label: 'Border', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Background', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'side-insert-wyrmcron': [
      { label: 'Border', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Background', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],
    'lid-insert-wyrmcron': [
      { label: 'Border', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Background', hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', hex: '#A6A9AA', name: 'Silver' }
    ],

    // Spiral — full pattern build. All 4 real zones/materials from every
    // role's source .3mf (verified via mf3.parse_3mf, and confirmed via
    // Kabsch solve for the tower-insert placement) are customer-
    // configurable. materialIndex 0/1 (Bottom/Middle of Spiral) are correct
    // and stable. materialIndex 2/3 for Top of Spiral/Pattern were SWAPPED
    // from an earlier version of this file, which had them the other way
    // around based on build_pattern.py's SPIRAL_ZONE_LABELS naming — that
    // turned out not to match the actual GLB's material order once checked
    // against the live 3D preview (confirmed by the store owner directly:
    // the "Top of Spiral" swatch was visibly recoloring the Pattern
    // geometry and vice versa). materialIndex: 2 is Top of Spiral and
    // materialIndex: 3 is Pattern. The ARRAY order here (this object's own
    // top-to-bottom order, Pattern listed last) is the separate, unrelated
    // color-picker DISPLAY order from an earlier request ("swap the pattern
    // zone and top of spiral zone, so the pattern zone comes last") and is
    // unchanged by this materialIndex fix. This only works because
    // recolor() (below) now keys materials by `materialIndex`, not array
    // position — see its own comment before changing this again. All 4
    // default to Silver, matching every other zone (Pattern originally
    // defaulted to Yellow, its baked filament color from the pilot build,
    // but the store owner later asked for Silver everywhere except the
    // Standard front-top Logo zone and the personal tray). side-insert-
    // spiral is shared by both Left and Right Side Insert cards, matching
    // every other pattern's convention (single standalone 3MF file
    // embedded twice in the tower).
    'front-bottom-insert-spiral': [
      { label: 'Bottom of Spiral', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle of Spiral', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Top of Spiral', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'front-top-insert-spiral': [
      { label: 'Bottom of Spiral', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle of Spiral', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Top of Spiral', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'side-insert-spiral': [
      { label: 'Bottom of Spiral', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle of Spiral', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Top of Spiral', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'lid-insert-spiral': [
      { label: 'Bottom of Spiral', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle of Spiral', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Top of Spiral', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ]
  };

  // Every color option on the shop's exact-match products lists "Jade
  // White" first (confirmed against the live product data) — the store
  // owner wants Silver as the default everywhere instead (matching the
  // configurator's own Silver-everywhere default), except the Standard
  // Front Top Panel's Logo Color, which stays Black, and the personal tray
  // (handled separately via ZONE_DEFS above, not a real Shopify option).
  // "Color" covers every single-option exact-match product (Back Panel,
  // Front Bottom Panel, vault Base/Lid, etc); "Panel Color" is the Standard
  // Front Top Panel's non-logo option specifically.
  var DEFAULT_OPTION_VALUE_OVERRIDES = {
    'Color': 'Silver',
    'Panel Color': 'Silver',
    'Logo Color': 'Black'
  };

  function srgbToLinear(c) {
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }

  function hexToFactor(hex) {
    var m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!m) return [1, 1, 1, 1];
    return [
      srgbToLinear(parseInt(m[1], 16) / 255),
      srgbToLinear(parseInt(m[2], 16) / 255),
      srgbToLinear(parseInt(m[3], 16) / 255),
      1
    ];
  }

  // model-viewer pauses its internal render loop while off-screen (its own
  // built-in IntersectionObserver-driven behavior, separate from ours) —
  // and confirmed via direct testing, coming back on-screen after that can
  // leave its LIVE camera silently reset to the library's own hardcoded
  // default (0deg/75deg) while the declared camera-orbit ATTRIBUTE is
  // untouched and still reads correctly. Confirmed empirically (a closed-
  // source model-viewer internal timing quirk, not something reachable
  // from our own code): re-writing the SAME already-correct value plus
  // jumpCameraToGoal() doesn't reliably recover it on the very next frame
  // either — during fast continuous scrolling the reset can recur several
  // times as the element's real on/off-screen state flickers faster than
  // a single follow-up catches. Re-asserting for a short run of frames
  // (~200ms) instead of just one closes that race. Cheap and side-effect-
  // free either way: it never changes the declared value, only re-commits
  // it, so calling it far more than necessary is harmless.
  function recommitCameraOrbit(mv) {
    if (!mv || typeof mv.jumpCameraToGoal !== 'function') return;
    var orbit = mv.getAttribute('camera-orbit');
    var framesLeft = 12; // ~200ms of safety margin at 60fps
    function reassert() {
      if (orbit) mv.cameraOrbit = orbit;
      mv.jumpCameraToGoal();
      framesLeft--;
      if (framesLeft > 0) requestAnimationFrame(reassert);
    }
    reassert();
  }

  function applyFlatColor(material, hex) {
    try {
      if (material.pbrMetallicRoughness.baseColorTexture) {
        material.pbrMetallicRoughness.baseColorTexture.setTexture(null);
      }
    } catch (e) { /* no texture to strip — fine */ }
    material.pbrMetallicRoughness.setBaseColorFactor(hexToFactor(hex));
  }

  function buildSwatchButton(color, isSelected, onClick) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dice-configurator__part-swatch' + (isSelected ? ' is-selected' : '');
    btn.setAttribute('aria-label', color.name);

    var circle = document.createElement('span');
    circle.className = 'dice-configurator__part-swatch-circle';
    circle.style.setProperty('--swatch-color', color.hex);
    circle.setAttribute('aria-hidden', 'true');

    var label = document.createElement('span');
    label.className = 'dice-configurator__part-swatch-label';
    label.textContent = color.name;

    btn.appendChild(circle);
    btn.appendChild(label);
    btn.addEventListener('click', onClick);
    return btn;
  }

  // F1.1 fix: this page previously only showed "Added to cart!" text next
  // to the button it was clicked on — the header cart bubble/drawer never
  // updated, so a customer adding several individual parts in a row saw a
  // stale cart count until their next full page load. Reusing the same
  // cart-notification/cart-drawer + `sections` param + renderContents()
  // path product-form.js already uses (see that file's onSubmitHandler)
  // fixes both: the drawer opens (or notification shows) AND the header
  // bubble repaints, matching what happens everywhere else on the site.
  var cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  function addToCart(item, statusEl, btn) {
    btn.disabled = true;
    if (statusEl) statusEl.textContent = 'Adding to cart…';

    var routeRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    // Bug fix: this page only ever adds ONE item per click, but posting it
    // wrapped as `{items: [item]}` makes Shopify's /cart/add.js respond
    // with the MULTI-item shape (`{items: [...]}`), not the flat
    // single-item object (`{id, key, quantity, ...}` at the top level)
    // that CartNotification.renderContents()'s `this.cartItemKey =
    // parsedState.key` expects — key came back undefined, its
    // cart-notification-product section lookup then matched nothing and
    // THREW, aborting that forEach loop before it ever reached the
    // cart-icon-bubble section update later in the same list. That's why
    // the header count stayed stale until a full page reload. Posting the
    // flat (non-array) shape Shopify's own product-form.js already uses
    // gets the response shape renderContents() actually expects.
    var payload = { id: item.id, quantity: item.quantity };
    if (item.properties) payload.properties = item.properties;
    if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
      payload.sections = cartEl.getSectionsToRender().map(function (section) { return section.id; });
      payload.sections_url = window.location.pathname;
    }

    fetch(routeRoot + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw new Error(err.description || 'Add to cart failed'); });
        return res.json();
      })
      .then(function (response) {
        btn.disabled = false;
        if (statusEl) statusEl.textContent = 'Added to cart!';
        if (cartEl && typeof cartEl.renderContents === 'function') {
          if (cartEl.setActiveElement) cartEl.setActiveElement(btn);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dice-tower-shop', cartData: response });
          }
          cartEl.renderContents(response);
        }
      })
      .catch(function (err) {
        btn.disabled = false;
        if (statusEl) statusEl.textContent = err.message || 'Something went wrong — please try again.';
      });
  }

  // Loads the card's deferred <product-model> and calls back once the
  // underlying <model-viewer> has actually parsed the glTF (materials
  // aren't available before its 'load' event) — same pattern as
  // refreshVisibleParts() in dice-tower-configurator.js. Camera ANGLE
  // itself is baked directly into the <model-viewer> tag's camera-orbit
  // attribute by Liquid (see dice-tower-shop-part-card.liquid /
  // dice-tower-shop-zone-part-card.liquid) — there's no angle value left
  // for JS to apply here. jumpCameraToGoal() below doesn't change that
  // value at all; it only commits it instantly. Without this call,
  // model-viewer resolves its "auto" distance (the radius needed to fit
  // the model, unknowable until the glTF's real bounding box is parsed)
  // with its own default smoothing animation, which reads as the card
  // drifting to its final zoom right after it finishes loading.
  function loadAndWait(card, cb) {
    var productModel = card.querySelector('product-model');
    if (!productModel) return;
    if (!productModel.getAttribute('loaded')) productModel.loadContent(false);
    var waitForViewer = function () {
      var mv = productModel.querySelector('model-viewer');
      if (!mv) { requestAnimationFrame(waitForViewer); return; }
      // model-viewer's own `model-visibility` event fires exactly when ITS
      // internal (IntersectionObserver-based, viewport-threshold-based —
      // not ours) visibility check flips, which is the precise moment its
      // pause/resume logic runs and can trigger the reset recommitCameraOrbit
      // documents. Our own observer in init() uses a 200px rootMargin
      // prefetch buffer, so its "intersecting" events don't line up with
      // model-viewer's own true on-screen transitions — this listens to
      // the real thing directly instead of approximating it.
      mv.addEventListener('model-visibility', function (e) {
        if (e.detail && e.detail.visible) recommitCameraOrbit(mv);
      });
      if (mv.model) {
        card.__diceMv = mv;
        recommitCameraOrbit(mv);
        cb(mv);
        return;
      }
      mv.addEventListener('load', function () {
        card.__diceMv = mv;
        recommitCameraOrbit(mv);
        cb(mv);
      }, { once: true });
    };
    waitForViewer();
  }

  function initPartCard(card) {
    var dataEl = card.querySelector('[data-shop-product-data]');
    if (!dataEl) return;
    var data = JSON.parse(dataEl.textContent);
    var rowsWrap = card.querySelector('[data-shop-swatch-rows]');
    var priceEl = card.querySelector('[data-shop-price]');
    var addBtn = card.querySelector('[data-shop-add-to-cart]');
    var statusEl = card.querySelector('[data-shop-status]');
    var selections = {}; // option position -> selected value name
    var currentVariant = null;
    var mvRef = null;

    function findVariant() {
      return data.variants.filter(function (v) {
        return data.options.every(function (opt) {
          return v.options[opt.position - 1] === selections[opt.position];
        });
      })[0] || null;
    }

    function recolor() {
      if (!mvRef || !mvRef.model) return;
      var mats = mvRef.model.materials;
      if (data.options.length === 1) {
        var hex = COLOR_HEX[selections[1]];
        mats.forEach(function (m) { applyFlatColor(m, hex); });
      } else {
        data.options.forEach(function (opt) {
          var mat = mats[opt.position - 1];
          if (mat) applyFlatColor(mat, COLOR_HEX[selections[opt.position]]);
        });
      }
    }

    function refresh() {
      currentVariant = findVariant();
      if (currentVariant) {
        priceEl.textContent = currentVariant.price;
        addBtn.disabled = false;
      } else {
        addBtn.disabled = true;
      }
      recolor();
    }

    data.options.forEach(function (opt) {
      var wantedDefault = DEFAULT_OPTION_VALUE_OVERRIDES[opt.name];
      selections[opt.position] = (wantedDefault && opt.values.indexOf(wantedDefault) !== -1) ? wantedDefault : opt.values[0];

      if (data.options.length > 1) {
        var heading = document.createElement('p');
        heading.className = 'dice-configurator__zone-row-label';
        heading.textContent = opt.name;
        rowsWrap.appendChild(heading);
      }

      var row = document.createElement('div');
      row.className = 'dice-configurator__part-swatch-row';
      opt.values.forEach(function (valueName) {
        var color = { name: valueName, hex: COLOR_HEX[valueName] || '#cccccc' };
        var btn = buildSwatchButton(color, valueName === selections[opt.position], function () {
          selections[opt.position] = valueName;
          row.querySelectorAll('.dice-configurator__part-swatch').forEach(function (b) {
            b.classList.toggle('is-selected', b === btn);
          });
          refresh();
        });
        row.appendChild(btn);
      });
      rowsWrap.appendChild(row);
    });

    addBtn.addEventListener('click', function () {
      if (!currentVariant) return;
      addToCart({ id: currentVariant.id, quantity: 1 }, statusEl, addBtn);
    });

    loadAndWait(card, function (mv) {
      mvRef = mv;
      refresh();
    });
  }

  function initZoneCard(card) {
    var zoneKey = card.dataset.zoneKey;
    var zones = ZONE_DEFS[zoneKey];
    if (!zones) return;
    var variantId = parseInt(card.dataset.variantId, 10);
    var rowsWrap = card.querySelector('[data-shop-zone-rows]');
    var addBtn = card.querySelector('[data-shop-add-to-cart]');
    var statusEl = card.querySelector('[data-shop-status]');
    var zoneColors = zones.map(function (z) { return { name: z.name, hex: z.hex }; });
    var mvRef = null;

    function recolor() {
      if (!mvRef || !mvRef.model) return;
      zoneColors.forEach(function (zc, i) {
        // materialIndex decouples this row's DISPLAY order (its position in
        // `zones`, free to differ — see Spiral's own doc comment in
        // ZONE_DEFS above) from the GLB's actual fixed material order.
        // Falls back to plain position for roles that don't declare it.
        var materialIndex = (zones[i] && zones[i].materialIndex !== undefined) ? zones[i].materialIndex : i;
        var mat = mvRef.model.materials[materialIndex];
        if (mat) applyFlatColor(mat, zc.hex);
      });
    }

    zones.forEach(function (zone, zoneIndex) {
      var heading = document.createElement('p');
      heading.className = 'dice-configurator__zone-row-label';
      heading.textContent = zone.label;
      var row = document.createElement('div');
      row.className = 'dice-configurator__part-swatch-row';
      COLORS.forEach(function (color) {
        var btn = buildSwatchButton(color, color.name === zone.name, function () {
          zoneColors[zoneIndex] = { name: color.name, hex: color.hex };
          row.querySelectorAll('.dice-configurator__part-swatch').forEach(function (b) {
            b.classList.toggle('is-selected', b === btn);
          });
          recolor();
        });
        row.appendChild(btn);
      });
      var wrap = document.createElement('div');
      wrap.className = 'dice-configurator__zone-row';
      wrap.appendChild(heading);
      wrap.appendChild(row);
      rowsWrap.appendChild(wrap);
    });

    addBtn.addEventListener('click', function () {
      var colorsText = zones.map(function (zone, i) { return zone.label + ': ' + zoneColors[i].name; }).join(', ');
      // `_zone_hex` is the same color data as `Colors` above, just as a
      // plain hex array instead of a "Label: Name" string — the cart
      // page's 3D preview (assets/cart-3d-preview.js) applies
      // materials[i] = hex[i] directly with no materialIndex concept of
      // its own, so this array MUST be built in true material-index
      // order here (not display/`zones` order, which can differ — see
      // Spiral's own doc comment in ZONE_DEFS above), or the cart preview
      // would show colors on the wrong parts even though the picker UI
      // itself is correct.
      var zoneHex = [];
      zones.forEach(function (zone, i) {
        var materialIndex = (zone.materialIndex !== undefined) ? zone.materialIndex : i;
        zoneHex[materialIndex] = zoneColors[i].hex;
      });
      addToCart({ id: variantId, quantity: 1, properties: { Colors: colorsText, _zone_hex: JSON.stringify(zoneHex) } }, statusEl, addBtn);
    });

    loadAndWait(card, function (mv) {
      mvRef = mv;
      recolor();
    });
  }

  function init() {
    var cards = document.querySelectorAll('[data-shop-part-card], [data-shop-zone-card]');
    if (!cards.length) return;

    // Stays subscribed after the first load (never unobserve) — scrolling a
    // loaded card back on-screen re-triggers model-viewer's own internal
    // pause/resume cycle, which is exactly what leaves its live camera
    // reset to the library default (see recommitCameraOrbit). Re-committing
    // on every re-intersection, not just the first one, is what fixes that
    // for good instead of only ever fixing the initial load.
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var card = entry.target;
        if (card.dataset.diceInited) {
          recommitCameraOrbit(card.__diceMv);
          return;
        }
        card.dataset.diceInited = 'true';
        if (card.hasAttribute('data-shop-part-card')) {
          initPartCard(card);
        } else {
          initZoneCard(card);
        }
      });
    }, { rootMargin: '200px 0px' });

    cards.forEach(function (card) { observer.observe(card); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
